import { Controller } from "@hotwired/stimulus"

// The donation landing page's benefactors carousel (TMS-1795): a deck of
// cards, the front one in full and the next two peeking out above it. Each
// card's data-depth (0 front, 1 and 2 behind, 3 out of sight) places it; CSS
// animates the change. It advances every few seconds, pauses while the deck is
// hovered or holds focus, and never advances on its own under reduced motion.
// The arrows, dots and Left/Right keys move it by hand, wrapping at the ends.
const ADVANCE_EVERY_MS = 6000
const VISIBLE_DEPTH = 3

export default class extends Controller {
  static targets = ["card", "dot"]

  connect() {
    this.index = 0
    this.paused = false
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.render()
    this.start()
  }

  disconnect() {
    this.stop()
  }

  next() {
    this.show(this.index + 1)
  }

  previous() {
    this.show(this.index - 1)
  }

  goTo(event) {
    this.show(Number(event.currentTarget.dataset.index))
  }

  keydown(event) {
    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
    }
  }

  pause() {
    this.paused = true
    this.stop()
  }

  // Focus moving between the deck's own controls fires focusout then focusin;
  // only leaving the deck altogether resumes it.
  resume(event) {
    if (event?.type === "focusout" && this.element.contains(event.relatedTarget)) return
    if (event?.type === "mouseleave" && this.element.contains(document.activeElement)) return

    this.paused = false
    this.start()
  }

  show(index) {
    const count = this.cardTargets.length
    this.index = ((index % count) + count) % count
    this.render()
    // A manual move restarts the wait, so the card just chosen gets its full turn.
    if (!this.paused) this.start()
  }

  render() {
    const count = this.cardTargets.length
    this.cardTargets.forEach((card, position) => {
      const depth = (position - this.index + count) % count
      const front = depth === 0
      card.dataset.depth = Math.min(depth, VISIBLE_DEPTH)
      card.toggleAttribute("inert", !front)
      if (front) {
        card.removeAttribute("aria-hidden")
      } else {
        card.setAttribute("aria-hidden", "true")
      }
    })

    this.dotTargets.forEach((dot, position) => {
      const active = position === this.index
      dot.classList.toggle("is-active", active)
      if (active) {
        dot.setAttribute("aria-current", "true")
      } else {
        dot.removeAttribute("aria-current")
      }
    })
  }

  start() {
    this.stop()
    if (this.reduceMotion.matches || this.paused) return

    this.timer = setInterval(() => {
      this.index = (this.index + 1) % this.cardTargets.length
      this.render()
    }, ADVANCE_EVERY_MS)
  }

  stop() {
    clearInterval(this.timer)
    this.timer = null
  }
}
